import { useState, useEffect } from 'react'
import { Calendar, Users, Clock, MapPin, Building2, Wifi, WifiOff, RefreshCw, ShieldCheck, Shield, MessageCircle, History } from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '../../services/api'

const fmtDate = (s) => {
  if (!s) return '—'
  try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s }
}
const fmtDateTime = (s) => {
  if (!s) return 'Nunca'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  } catch { return s }
}
const timeAgo = (s) => {
  if (!s) return 'Nunca logou'
  const diff = Date.now() - new Date(s).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Agora'
  if (mins < 60) return `${mins}min atrás`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h atrás`
  const days = Math.floor(hours / 24)
  return `${days}d atrás`
}

export default function SuperAdminDashboard() {
  const [leiloes, setLeiloes] = useState([])
  const [logins, setLogins] = useState([])
  const [loadingL, setLoadingL] = useState(true)
  const [loadingU, setLoadingU] = useState(true)

  const fetchLeiloes = () => {
    setLoadingL(true)
    api.get('/auth/super-admin/leiloes-hoje')
      .then(r => setLeiloes(r.data))
      .catch(() => setLeiloes([]))
      .finally(() => setLoadingL(false))
  }

  const fetchLogins = () => {
    setLoadingU(true)
    api.get('/auth/super-admin/login-log')
      .then(r => setLogins(r.data))
      .catch(() => setLogins([]))
      .finally(() => setLoadingU(false))
  }

  useEffect(() => {
    fetchLeiloes()
    fetchLogins()
  }, [])

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Painel Super Admin</h1>
          <p className="text-gray-500 text-sm mt-1">Visão geral do sistema</p>
        </div>
        <button
          onClick={() => { fetchLeiloes(); fetchLogins() }}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw size={14} />
          Atualizar
        </button>
      </div>

      {/* Leilões Hoje */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-800">Leilões Hoje</h2>
            <p className="text-xs text-gray-500">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <span className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">
            {leiloes.length}
          </span>
        </div>

        {loadingL ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : leiloes.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            Nenhum leilão agendado para hoje
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {leiloes.map(l => (
              <div key={l.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${l.status === 'on' ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{l.nome}</div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Building2 size={12} />
                      {l.organizador_nome}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {l.cidade}/{l.uf}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                    <Clock size={14} />
                    {l.hora_inicio}
                  </div>
                  <div className={`text-xs mt-0.5 ${l.status === 'on' ? 'text-green-600' : 'text-gray-400'}`}>
                    {l.status === 'on' ? '● Ativo' : '○ Inativo'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Log de Logins */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Users className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-800">Último Login dos Usuários</h2>
            <p className="text-xs text-gray-500">
              {logins.filter(u => u.online).length} online agora · {logins.filter(u => u.last_login).length} com login registrado
            </p>
          </div>
          <Link
            to="/super-admin/todos-logins"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History size={14} /> Ver histórico completo
          </Link>
        </div>

        {loadingU ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 font-medium text-gray-600">Usuário</th>
                  <th className="px-4 py-2.5 font-medium text-gray-600">Organizador</th>
                  <th className="px-4 py-2.5 font-medium text-gray-600">Nível</th>
                  <th className="px-4 py-2.5 font-medium text-gray-600">2FA</th>
                  <th className="px-4 py-2.5 font-medium text-gray-600">Último Login</th>
                  <th className="px-4 py-2.5 font-medium text-gray-600">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logins.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {u.online
                          ? <Wifi size={12} className="text-green-500" title="Online agora" />
                          : <WifiOff size={12} className="text-gray-300" title="Offline" />}
                        <div>
                          <div className="font-medium text-gray-900">{u.name}</div>
                          <div className="text-xs text-gray-400">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{u.organizador_nome || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        u.permission_level === 'super_admin' ? 'bg-red-100 text-red-700' :
                        u.permission_level === 'admin' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {u.permission_level}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {u.tem_dispositivo_confiavel ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            <ShieldCheck className="w-3 h-3" /> Verificado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                            <Shield className="w-3 h-3" /> Nunca
                          </span>
                        )}
                        {u.telefone && <MessageCircle className="w-3.5 h-3.5 text-green-600" title="WhatsApp cadastrado" />}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-gray-700">{timeAgo(u.last_login)}</div>
                      <div className="text-xs text-gray-400">{fmtDateTime(u.last_login)}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{u.last_login_ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
